import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useAuth from '../Hooks/useAuth';
import { API_ENDPOINTS } from '../Config/api';
import { Icon } from '../Components/UI/Icon';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ReactComponent as ASightLogo } from '../Assets/ASightLogo.svg';
import { useRecoilValue } from 'recoil';
import { langAtom, changeLanguage } from '../Atom/langAtom';
import itFlag from '../Assets/it.svg';
import enFlag from '../Assets/gb.svg';
import ptFlag from '../Assets/pt.svg';
import LanguageSelector from '../Components/LanguageSelector';
import MFAInput from '../Components/MFAInput';
import { useTranslation } from 'react-i18next';
import { getHostRef } from '../Hooks/useChannel';
import useTurnstile from '../Hooks/useTurnstile';

// Client-side login rate limiting: exponential backoff after failed attempts
const MAX_ATTEMPTS_BEFORE_LOCKOUT = 5;
const BASE_LOCKOUT_MS = 2000; // 2 seconds

export default function Login() {
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [formError, setFormError] = useState('');
  const [showReset, setShowReset] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState(null);
  const [refBranding, setRefBranding] = useState(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaError, setMfaError] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);

  const { loading, postData } = useAuth(API_ENDPOINTS.token);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const language = useRecoilValue(langAtom);
  const { t } = useTranslation();
  const { enabled: captchaEnabled, token: captchaToken, resetTurnstile, TurnstileWidget } = useTurnstile();

  const refCode = useMemo(() => getHostRef() || searchParams.get('ref'), [searchParams]);

  // Persist ref in localStorage for store flow
  useEffect(() => {
    if (refCode) {
      localStorage.setItem('asight_channel', refCode);
    }
  }, [refCode]);

  // Fetch branding when ref is present (from hostname or query param)
  useEffect(() => {
    if (!refCode) return;
    fetch(`${API_ENDPOINTS.brandingLogo}?ref=${encodeURIComponent(refCode)}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data && data.white_label && data.logo_url) {
          setRefBranding(data);
        }
      })
      .catch(() => {});
  }, [refCode]);

  // Language selection is handled by LanguageSelector component

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    // Exponential backoff: block login if locked out
    if (lockedUntil && Date.now() < lockedUntil) {
      const secondsLeft = Math.ceil((lockedUntil - Date.now()) / 1000);
      setFormError(t('login.tooManyAttempts', { seconds: secondsLeft }) || `Troppi tentativi. Riprova tra ${secondsLeft} secondi.`);
      return;
    }

    const { data: responseData, error: postError } = await postData({ username, password, ref: refCode || '', captcha_token: captchaToken });

    if (!postError && responseData) {
      // Check if MFA is required
      if (responseData.mfa_required && responseData.mfa_token) {
        setMfaRequired(true);
        setMfaToken(responseData.mfa_token);
        setMfaError('');
        return;
      }
      setFailedAttempts(0);
      setLockedUntil(null);
      // Store ref_channel from backend for logout redirect
      if (responseData.ref_channel) {
        sessionStorage.setItem('ref_channel', responseData.ref_channel);
      }
      // Se l'azienda richiede MFA obbligatorio e l'utente non lo ha attivo, redirect a setup
      if (responseData.mfa_setup_required) {
        navigate('/mfa-setup');
        return;
      }
      navigate('/dashboard');
    } else {
      // Check if 403 with ref_channel (user needs partner portal)
      if (postError?.responseData?.ref_channel) {
        setFormError(t('login.partnerAccessOnly', 'Accesso consentito solo tramite il portale del tuo partner.'));
        return;
      }
      const newAttempts = failedAttempts + 1;
      setFailedAttempts(newAttempts);
      if (newAttempts >= MAX_ATTEMPTS_BEFORE_LOCKOUT) {
        const lockoutMs = BASE_LOCKOUT_MS * Math.pow(2, newAttempts - MAX_ATTEMPTS_BEFORE_LOCKOUT);
        setLockedUntil(Date.now() + lockoutMs);
      }
      setFormError(t('login.invalidCredentials'));
      resetTurnstile();
    }
  };

  const handleMfaVerify = async (otpCode) => {
    setMfaError('');
    setMfaLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.mfaVerify, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfa_token: mfaToken, otp_code: otpCode }),
      });
      const json = await res.json();
      if (res.ok && json.access) {
        sessionStorage.setItem('access_token', json.access);
        if (json.refresh) sessionStorage.setItem('refresh_token', json.refresh);
        setFailedAttempts(0);
        setLockedUntil(null);
        navigate('/dashboard');
      } else {
        setMfaError(json.detail || t('login.invalidOtp', 'Codice OTP non valido.'));
      }
    } catch {
      setMfaError(t('login.networkError', 'Errore di rete.'));
    } finally {
      setMfaLoading(false);
    }
  };

  const handleResetRequest = async (e) => {
    e.preventDefault();
    setResetMsg('');

    try {
      const res = await fetch(API_ENDPOINTS.forgotPassword, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail, captcha_token: captchaToken }),
      });

      if (res.ok) {
        setResetMsg(t('login.emailSent'));
        setResetEmail('');
      } else {
        setResetMsg(t('login.resetError'));
      }
    } catch {
      setResetMsg('Errore di rete.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row overflow-hidden">
  {/* Sezione immagine */}
      <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
        className="relative md:w-1/2 w-full h-[30vh] md:min-h-screen overflow-hidden bg-gradient-to-b md:bg-gradient-to-r from-yellow-400 via-yellow-100 to-white"
      >
        {/* language toggle moved into form area so it's aligned with the Login title */}

        <div className="absolute inset-0 flex items-center justify-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="text-black text-3xl md:text-4xl font-bold text-center px-4"
          >
            {refBranding ? (
              <img src={refBranding.logo_url} alt={refBranding.name || 'Partner'} draggable="false" className="h-32 w-auto max-w-[320px] object-contain drop-shadow-lg" style={{ background: 'transparent', userSelect: 'none' }} />
            ) : (
              <ASightLogo className="h-10 w-auto" />
            )}
          </motion.h2>
        </div>
      </motion.div>


      {/* Sezione form */}
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
        className="md:w-1/2 w-full flex flex-col items-center justify-center p-8 bg-white overflow-y-auto"
      >
        {/* Toggle Accedi / Registrati */}
        <div className="w-full max-w-md mb-6">
          <div className="flex rounded-lg bg-gray-100 p-1">
            <button
              className="flex-1 py-2 text-sm font-semibold rounded-md transition-all duration-200 bg-yellow-500 text-white shadow-sm"
            >
              {t('login.loginButton', 'Accedi')}
            </button>
            <button
              onClick={() => navigate(refCode ? `/store/ordine?ref=${encodeURIComponent(refCode)}` : '/store/ordine')}
              className="flex-1 py-2 text-sm font-semibold rounded-md transition-all duration-200 text-gray-500 hover:text-gray-700"
            >
              {t('login.registerCompany', 'Registrati')}
            </button>
          </div>
        </div>

        <div className="w-full max-w-md space-y-6">
          <AnimatePresence mode="wait">
                {mfaRequired ? (
              <motion.div
                key="mfa"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.4 }}
                className="space-y-4"
              >
                <h2 className="text-3xl font-bold text-gray-800">{t('login.mfaTitle', 'Verifica identità')}</h2>
                <p className="text-gray-500">{t('login.mfaSubtitle', 'Inserisci il codice a 6 cifre dalla tua app Google Authenticator.')}</p>
                <MFAInput
                  onComplete={handleMfaVerify}
                  disabled={mfaLoading}
                  error={mfaError}
                />
                {mfaLoading && (
                  <div className="flex justify-center">
                    <Icon name="loader" className="animate-spin text-yellow-600" />
                  </div>
                )}
                <button
                  onClick={() => { setMfaRequired(false); setMfaToken(null); setMfaError(''); }}
                  className="text-sm text-yellow-600 hover:underline block text-center"
                >
                  {t('login.backToLogin', 'Torna al login')}
                </button>
              </motion.div>
            ) : showReset ? (
                <motion.div
                key="reset"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.4 }}
                className="space-y-4"
              >
                <h2 className="text-3xl font-bold text-gray-800">{t('login.resetTitle')}</h2>
                <p className="text-gray-500">{t('login.resetInstructions')}</p>
                <form onSubmit={handleResetRequest} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('login.emailLabel')}</label>
                    <input
                      type="email"
                      className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring focus:border-yellow-500"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                    />
                  </div>
                  {resetMsg && (
                    <p className="text-sm text-center text-gray-600">{resetMsg}</p>
                  )}
                  <button
                    type="submit"
                    className="w-full py-2 px-4 bg-yellow-600 text-white font-semibold rounded-md hover:bg-yellow-700 transition"
                  >
                    {t('login.sendResetButton')}
                  </button>
                  {TurnstileWidget}
                </form>
                <button
                  onClick={() => { setFormError(false); setShowReset(false) }}
                  className="text-sm text-yellow-600 hover:underline block text-center"
                >
                  {t('login.backToLogin')}
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="login"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.4 }}
                className="space-y-4"
              >
                <div className="flex items-center justify-between">
                  <h2 className="text-3xl font-bold text-gray-800">{t('login.title')}</h2>
                  {/* Language selector with flag aligned to the right of the title */}
                  <div className="flex items-center gap-2">
                    <LanguageSelector />
                  </div>
                </div>
                <p className="text-gray-500">{t('login.subtitle')}</p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('login.username')}</label>
                    <input
                      type="email"
                      className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring focus:border-yellow-500"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">{t('login.password')}</label>
                    <input
                      type="password"
                      className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring focus:border-yellow-500"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading || (captchaEnabled && !captchaToken)}
                    className={`w-full py-2 px-4 flex justify-center items-center gap-2 
                      ${loading ? 'bg-yellow-400 cursor-not-allowed' : 'bg-yellow-600 hover:bg-yellow-700'} 
                      text-white font-semibold rounded-md transition`}
                  >
                    {loading ? (
                      <>
                        <Icon name="loader" className="animate-spin" />
                        {t('login.loading')}
                      </>
                    ) : (
                      t('login.loginButton')
                    )}
                  </button>
                  {TurnstileWidget}
                </form>
                <button
                  onClick={() => { setFormError(false); setShowReset(true) }}
                  className="text-sm text-yellow-600 hover:underline block text-center"
                >
                  {t('login.forgotPassword')}
                </button>
              </motion.div>


            )}
          </AnimatePresence>
          {formError && (
            <div className="text-sm text-red-600 font-medium bg-red-100 border border-red-300 p-2 rounded">
              {formError}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
